import { useEffect, useState } from 'react'
import { CopyFallbackDialog } from '../components/CopyFallbackDialog'
import { buildFeedbackSummary, buildLocalDataExport } from '../data/localExport'
import { useLocalOverview } from '../hooks/useLocalData'
import type { LocalDataExport } from '../types/localExport'

const getThemePreference = (): LocalDataExport['preferences']['theme'] => {
  const value = localStorage.getItem('daily-read-theme')
  return value === 'light' || value === 'dark' ? value : 'system'
}

export function SettingsPage() {
  const { data: counts, loading, error, refresh } = useLocalOverview()
  const [working, setWorking] = useState(false)
  const [toast, setToast] = useState<string>()
  const [copyFallback, setCopyFallback] = useState<string>()

  useEffect(() => {
    if (!toast) return
    const timeout = window.setTimeout(() => setToast(undefined), 2400)
    return () => window.clearTimeout(timeout)
  }, [toast])

  const exportData = async () => {
    setWorking(true)
    try {
      const data = await buildLocalDataExport(new Date().toISOString(), getThemePreference())
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `daily-read-data-${data.exportedAt.replaceAll(':', '-').replaceAll('.', '-')}.json`
      link.click()
      window.setTimeout(() => URL.revokeObjectURL(url), 0)
      setToast('完整本地数据已导出')
    } catch {
      setToast('导出失败，请重试')
    } finally {
      setWorking(false)
    }
  }

  const copySummary = async () => {
    setWorking(true)
    try {
      const value = await buildFeedbackSummary()
      try {
        if (!navigator.clipboard) throw new Error('Clipboard API unavailable')
        await navigator.clipboard.writeText(value)
        setToast('反馈摘要已复制，可粘贴给 Codex')
      } catch {
        setCopyFallback(value)
      }
    } catch {
      setToast('生成摘要失败，请重试')
    } finally {
      setWorking(false)
    }
  }

  if (loading) return <section className="loading-state" aria-label="正在读取设置"><span /><span /><span /></section>
  if (error) return <section className="empty-state page-enter"><p className="eyebrow">Data & feedback</p><h1>暂时无法读取你的数据</h1><p>请重试；现有收藏、反馈和学习进度不会因此丢失。</p><button className="primary-button" type="button" onClick={() => void refresh()}>重试</button></section>

  return (
    <div className="local-page page-enter">
      <header className="local-hero"><p className="eyebrow">Data & feedback</p><h1>数据备份与反馈</h1><p>备份你的阅读记录和词汇进度，也可以把阅读偏好交给 Codex，用于调整后续日报。</p></header>
      <aside className="local-only-notice" role="note"><strong>设备本地数据</strong><p>清除浏览器站点数据或卸载 PWA 可能删除收藏、反馈、阅读记录与词汇进度。建议定期下载数据备份。</p></aside>

      <section className="stats-grid" aria-label="本地数据统计">
        <article><strong>{counts.savedItems}</strong><span>收藏</span></article>
        <article><strong>{counts.feedbackItems}</strong><span>反馈</span></article>
        <article><strong>{counts.readingRecords}</strong><span>阅读记录</span></article>
        <article><strong>{counts.vocabulary}</strong><span>唯一词语</span></article>
        <article><strong>{counts.pendingVocabulary}</strong><span>待补充释义</span></article>
        <article><strong>{counts.reviewedVocabulary}</strong><span>已复习词语</span></article>
      </section>

      <section className="settings-actions" aria-labelledby="data-portability-title">
        <div className="section-heading"><div><p className="eyebrow">Backup & feedback</p><h2 id="data-portability-title">备份与交接</h2></div></div>
        <article><div><h3>下载数据备份</h3><p>保存你的收藏、反馈、阅读记录和词汇进度，方便日后迁移或恢复。</p></div><button type="button" className="primary-button" disabled={working} onClick={() => void exportData()}>下载数据备份</button></article>
        <article><div><h3>复制给 Codex 的反馈摘要</h3><p>整理感兴趣与不感兴趣主题、最近代表条目、收藏数和近期词汇，方便你主动交给 Codex 调整下一期。</p></div><button type="button" className="primary-button" disabled={working} onClick={() => void copySummary()}>复制摘要</button></article>
      </section>

      <details className="settings-boundary"><summary>技术信息</summary><p>备份格式版本：1.1.0。数据只保存在当前浏览器，不会自动同步；清除站点数据或卸载 PWA 可能删除本地记录。</p><p>通用英文释义来自 <a href="https://github.com/skywind3000/ECDICT" target="_blank" rel="noreferrer noopener">ECDICT</a>（MIT License）；日报内的语境释义优先于通用词典。</p></details>
      <CopyFallbackDialog value={copyFallback} title="手动复制反馈摘要" label="给 Codex 的反馈摘要" onClose={() => setCopyFallback(undefined)} />
      {toast && <div className="action-toast" role="status">{toast}</div>}
    </div>
  )
}
